import type {Status as ConsentStatus} from '@/modules/common/context/Consent/models';
import type {LooseString} from '@/types';

import {useEffect, useState, useMemo} from 'react';

import {ConsentContext} from '@/modules/common/context/Consent/ConsentContext';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface needed for global declaration
  interface Window {
    // https://www.cookiebot.com/en/developer/
    CookieConsent?: {
      consent?: {
        stamp?: LooseString<'0'>; // 0 means no consent yet given
        marketing?: boolean;
        necessary?: boolean;
        preferences?: boolean;
        statistics?: boolean;
      };
    };
    // https://usercentrics.com/docs/web/features/api/interfaces/
    __ucCmp?: {
      cmpController?: {
        consent?: {
          status:
            | 'ALL_ACCEPTED'
            | 'ALL_DENIED'
            | 'SOME_ACCEPTED'
            | 'SOME_DENIED';
          serviceIds?: Array<string>;
          required: boolean;
          version: number;
          controllerId: string;
          language: string;
          createdAt: number;
          updatedAt: number;
          updatedBy:
            | 'onAcceptAllServices'
            | 'onDenyAllServices'
            | 'onEssentialChange'
            | 'onInitialPageLoad'
            | 'onNonEURegion'
            | 'onSessionRestored'
            | 'onTcfStringChange'
            | 'onUpdateServices'
            | 'onMobileSessionRestore';
          setting: {
            id: string;
            type: 'TCF' | 'GDPR' | 'CCPA';
            version: string;
            abVariant?: string;
            sandbox?: true;
          };
          type: 'IMPLICIT' | 'EXPLICIT';
          hash: string;
          gpcSignal?: boolean;
          isBot?: true;
          isOutsideEu?: true;
        };
        dps?: {
          categories: Record<
            string,
            {
              dps: Record<string, boolean> | null;
              essential?: boolean;
              hidden?: boolean;
              name: string;
              state: 'ALL_DENIED' | 'SOME_ACCEPTED' | 'ALL_ACCEPTED';
            }
          >;
        };
      };
    };
  }
}

const readCookieBotConsent = (): ConsentStatus | null => {
  // This information will be available after the user has made a decision and contains the consent status.
  const cookieBotConsent = window?.CookieConsent?.consent;

  if (
    typeof cookieBotConsent?.stamp === 'string' &&
    cookieBotConsent.stamp !== '0'
  ) {
    return {
      personalization: cookieBotConsent?.preferences ?? false,
      retargeting: cookieBotConsent.marketing ?? false,
      statistic: cookieBotConsent.statistics ?? false,
      technicalNecessary: cookieBotConsent.necessary ?? true,
    };
  }

  return null;
};

const readUsercentricsConsent = (): ConsentStatus | null => {
  const usercentricsCmp = window?.__ucCmp?.cmpController;

  if (
    usercentricsCmp &&
    // IMPLICIT = When the user didn't interact with the banner
    // EXPLICIT = When the user actively gives any type of consent (what we care about)
    usercentricsCmp.consent?.type === 'EXPLICIT' &&
    usercentricsCmp.dps?.categories
  ) {
    const categories = Object.fromEntries(
      Object.values(usercentricsCmp.dps.categories).map((category) => [
        // have to use the name shown in the ui as identifier
        category.name,
        category.state === 'ALL_ACCEPTED',
      ])
    );
    return {
      personalization: categories.Personalisierung,
      retargeting: categories.Retargeting,
      statistic: categories.Statistik,
      technicalNecessary: categories.Notwendig ?? true,
    };
  }

  return null;
};

const getConsentStatus = (): ConsentStatus | null => {
  return readCookieBotConsent() ?? readUsercentricsConsent() ?? null;
};

const POLLING_INTERVAL = 250;
const TIMEOUT = 60_000;

type Props = React.PropsWithChildren;

export const ConsentProvider: React.FunctionComponent<Props> = ({children}) => {
  const [status, setStatus] = useState<ConsentStatus | null>(() =>
    getConsentStatus()
  );

  useEffect(() => {
    let intervalId: number;
    let timeoutId: number;
    // Start polling until we know the consent status.
    // If it takes to long we stop checking and assume no consent.
    if (!status) {
      intervalId = window.setInterval(() => {
        const currentStatus = getConsentStatus();
        if (currentStatus) {
          setStatus(currentStatus);
          window.clearInterval(intervalId);
        }
      }, POLLING_INTERVAL);
      timeoutId = window.setTimeout(() => {
        window.clearInterval(intervalId);
      }, TIMEOUT);
    }
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  const contextValue = useMemo(
    () => ({
      status,
    }),
    [status]
  );

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};
